<!-- EditUniverseModal.vue -->
<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Load a Universe</p>
      <button
        type="button"
        class="delete"
        v-on:click="$parent.close()"/>
    </header>
    <div class="modal-card-body">
      <p>
        Select one of your portfolios, or any available fund, to use as your universe.
      </p>
      <b-tabs v-model="activeTab">
        <b-tab-item label="Portfolios">
          <!-- Only show this when the user genuinely has no portfolios (not merely
               when a search returned nothing) -->
          <p v-show="showEmptyMessage(modelPortfolios, searchQuery)">
            You have no Model Portfolios, please create one in the Advisor Dashboard.
          </p>
          <!-- Keep the search box + table visible whenever there are results OR a
               search is active, so an empty search result never hides the input -->
          <div v-show="showBrowser(modelPortfolios, searchQuery)">
            <p>Choose a Model Portfolio to use as a universe:</p>

            <!-- Search Input -->
            <b-field>
              <b-input
                v-model="searchQuery"
                placeholder="Search Portfolios..."
                @input="loadModelPortfolios(1, searchQuery)"
                debounce="500"
              />
            </b-field>

            <p v-show="showNoResultsMessage(modelPortfolios, searchQuery)">
              No Model Portfolios match "{{ searchQuery }}".
            </p>

            <!-- Table without built-in pagination -->
            <b-table
              v-bind:data="modelPortfolios.a"
              v-bind:loading="!modelPortfolios.loaded"
              :paginated="false"
              icon-pack="fas"
            >
              <b-table-column field="nickname" label="Name" sortable v-slot="props">
                {{ props.row.nickname }}
              </b-table-column>
              <b-table-column field="updated_at" label="Last Updated" sortable v-slot="props">
                {{ new Date(props.row.updated_at).toLocaleString() }}
              </b-table-column>
              <b-table-column field="company_count" label="Public Companies" sortable v-slot="props">
                {{ props.row.company_count }}
              </b-table-column>
              <b-table-column field="name" label="Loading Options" sortable v-slot="props">
                <b-tooltip label="This looks at directly held equities, and optimizations will also preserve weightings of loaded bond funds" position="is-left" multilined>
                  <a v-on:click="$root.$emit('universe-changed', 'model_portfolio_direct', props.row.id, props.row.nickname); $parent.close()">
                    Standard Loading
                  </a>
                </b-tooltip>
                <br>
                ------------
                <b-tooltip label="This aggregates the holdings of the equity funds into a universe of direct equities" position="is-left" multilined>
                  <a v-on:click="$root.$emit('universe-changed', 'model_portfolio', props.row.id, props.row.nickname); $parent.close()">
                    Lookthrough Fund Holdings
                  </a>
                </b-tooltip>
              </b-table-column>
            </b-table>

            <!-- Separate pagination component -->
            <b-pagination
              v-model="modelPortfolios.currentPage"
              :total="modelPortfolios.total"
              :per-page="10"
              @change="page => loadModelPortfolios(page, searchQuery)"
            />
          </div>
        </b-tab-item>

        <b-tab-item label="Client Portfolios">
          <!-- Only show this when the user genuinely has no client portfolios (not
               merely when a search returned nothing) -->
          <p v-show="showEmptyMessage(shareowners, shareownerSearchQuery)">
            You have no Client Portfolios with holdings.
          </p>
          <!-- Keep the search box + table visible whenever there are results OR a
               search is active, so an empty search result never hides the input -->
          <div v-show="showBrowser(shareowners, shareownerSearchQuery)">
            <p>Choose a Client Portfolio to use as a universe:</p>

            <!-- Search Input -->
            <b-field>
              <b-input
                v-model="shareownerSearchQuery"
                placeholder="Search Client Portfolios..."
                @input="loadShareowners(1, shareownerSearchQuery)"
                debounce="500"
              />
            </b-field>

            <p v-show="showNoResultsMessage(shareowners, shareownerSearchQuery)">
              No Client Portfolios match "{{ shareownerSearchQuery }}".
            </p>

            <!-- Table without built-in pagination -->
            <b-table
              v-bind:data="shareowners.a"
              v-bind:loading="!shareowners.loaded"
              :paginated="false"
              icon-pack="fas"
            >
              <b-table-column field="nickname" label="Name" sortable v-slot="props">
                {{ props.row.nickname }}
              </b-table-column>
              <b-table-column field="updated_at" label="Last Updated" sortable v-slot="props">
                {{ new Date(props.row.updated_at).toLocaleString() }}
              </b-table-column>
              <b-table-column field="company_count" label="Public Companies" sortable v-slot="props">
                {{ props.row.company_count }}
              </b-table-column>
              <b-table-column field="name" label="Loading Options" sortable v-slot="props">
                <b-tooltip label="This looks at directly held equities" position="is-left" multilined>
                  <a v-on:click="$root.$emit('universe-changed', 'shareowner_direct', props.row.id, props.row.nickname); $parent.close()">
                    Standard Loading
                  </a>
                </b-tooltip>
                <br>
                ------------
                <b-tooltip label="This aggregates the holdings of the equity funds into a universe of direct equities" position="is-left" multilined>
                  <a v-on:click="$root.$emit('universe-changed', 'shareowner', props.row.id, props.row.nickname); $parent.close()">
                    Lookthrough Fund Holdings
                  </a>
                </b-tooltip>
              </b-table-column>
            </b-table>

            <!-- Separate pagination component -->
            <b-pagination
              v-model="shareowners.currentPage"
              :total="shareowners.total"
              :per-page="10"
              @change="page => loadShareowners(page, shareownerSearchQuery)"
            />
          </div>
        </b-tab-item>

        <b-tab-item label="Funds" class="fund-search-tab">
          <TypeaheadFundSearch
            v-bind:callback="fundSelectCallback"
          />
          <br>
          <p class="help is-small">
            <strong>Disclaimer</strong><br>
          </p>
          <p class="help">
            Fund holdings are filed with the Securities and Exchange Commission on a quarterly basis, and are updated on YourStake within one month after the filing date. New funds and companies become accessible on YourStake when sufficient data is available. New funds become available on YourStake approximately one month after first filing their holdings with the Securities and Exchange Commission. New companies become available on YourStake approximately 6 months after their IPO.
          </p>
          <b-table
            v-bind:data="selectedFunds"
            icon-pack="fas"
          >
            <b-table-column field="ticker.symbol" label="Ticker" v-slot="props">
              {{ props.row.ticker.symbol }}
            </b-table-column>
            <b-table-column field="ticker.full_name" label="Full Name" v-slot="props">
              {{ props.row.ticker.full_name }}
            </b-table-column>
            <b-table-column v-slot="props">
              <button @click="unselectFund(props.row)" class="button is-small">
                Remove
              </button>
            </b-table-column>
          </b-table>
          <div class="button is-primary" @click="submitFunds">Submit</div>
        </b-tab-item>
      </b-tabs>
    </div>
  </div>
</template>

<script>

import TypeaheadFundSearch from '@frontend/shared/components/TypeaheadFundSearch.vue';
import * as universeDisplay from './universeModalDisplay';

export default {
  data() {
    return {
      activeTab: 0,
      searchQuery: '',
      shareownerSearchQuery: '',
      selectedFunds: [],
      modelPortfolios: {
        a: [],
        loaded: false,
        total: 0,
        currentPage: 1,
      },
      shareowners: {
        a: [],
        loaded: false,
        total: 0,
        currentPage: 1,
      },
    };
  },
  components: {
    TypeaheadFundSearch,
  },
  props: {
    parent: Object,
  },
  mounted() {
    this.loadModelPortfolios();
    this.loadShareowners();
  },
  methods: {
    // Display-state helpers shared by the Portfolios and Client Portfolios tabs.
    // `collection` is either `modelPortfolios` or `shareowners` ({ a, loaded }).
    showBrowser(collection, searchQuery) {
      return universeDisplay.shouldShowBrowser(collection.loaded, collection.a.length, searchQuery);
    },
    showEmptyMessage(collection, searchQuery) {
      return universeDisplay.shouldShowEmptyMessage(collection.loaded, collection.a.length, searchQuery);
    },
    showNoResultsMessage(collection, searchQuery) {
      return universeDisplay.shouldShowNoResultsMessage(collection.loaded, collection.a.length, searchQuery);
    },

    // Load portfolios with pagination & search
    loadModelPortfolios(page = 1, search = '') {
      this.modelPortfolios.loaded = false;
      const url = `/api/v1/model_portfolios/?page=${page}&page_size=10&search=${encodeURIComponent(search)}`;
      fetch(url)
          .then(response => {
            if (!response.ok) throw new Error(`Request failed: ${response.status}`);
            return response.json();
          })
          .then(data => {
            this.modelPortfolios.a = data.results || [];
            this.modelPortfolios.total = data.count || 0;
            this.modelPortfolios.currentPage = page;
            this.modelPortfolios.loaded = true;
          })
          .catch(error => {
            // On failure, surface an empty (loaded) state rather than leaving the
            // table stuck on a loading spinner or hiding the search input.
            console.error('Failed to load model portfolios', error);
            this.modelPortfolios.a = [];
            this.modelPortfolios.total = 0;
            this.modelPortfolios.loaded = true;
          });
    },

    // Load shareowners with pagination & search
    loadShareowners(page = 1, search = '') {
      this.shareowners.loaded = false;
      const url = `/api/v1/shareowners/?page=${page}&page_size=10&search=${encodeURIComponent(search)}`;
      fetch(url)
          .then(response => {
            if (!response.ok) throw new Error(`Request failed: ${response.status}`);
            return response.json();
          })
          .then(data => {
            this.shareowners.a = data.results || [];
            this.shareowners.total = data.count || 0;
            this.shareowners.currentPage = page;
            this.shareowners.loaded = true;
          })
          .catch(error => {
            console.error('Failed to load client portfolios', error);
            this.shareowners.a = [];
            this.shareowners.total = 0;
            this.shareowners.loaded = true;
          });
    },

    fundSelectCallback(fund) {
      if (this.selectedFunds.find(selectedFund => selectedFund.ticker.id === fund.ticker.id)) {
        return;
      }
      this.selectedFunds.push(fund);
    },
    unselectFund(fund) {
      this.selectedFunds = this.selectedFunds.filter(selectedFund => selectedFund.ticker.id !== fund.ticker.id);
    },
    submitFunds() {
      if (!this.selectedFunds.length) {
        this.$parent.close();
        return;
      }
      const tickerSymbols = this.selectedFunds.map(fund => fund.ticker.symbol);
      const stringToSend = tickerSymbols.join(',');
      const stringToDisplay = tickerSymbols.join('/');
      this.$root.$emit('universe-changed', 'fund', stringToSend, stringToDisplay);
      this.$parent.close();
    },
  },
};
</script>

<style lang="scss">
.fund-search-tab {
  min-height: 600px;
}
</style>
